import { useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { NucToast } from "src/reusable/components/Toast";
import { extractErrorDetail } from "../utils/errorDetail";
import {
  useCreatePartSchema,
  useUpdatePartSchema,
  useAddFeatures,
  useUpdateFeatures,
  useRemoveFeatures,
  type PartSchema,
  type PartSchemaFeatureCreate,
  type PartSchemaFeatureUpdateItem,
  type FeatureDataType,
} from "../services/PartSchemaService";

const DATA_TYPES: FeatureDataType[] = ["string", "integer", "float", "boolean"];

const featureSchema = z.object({
  // Present for features already persisted on an edited schema.
  id: z.number().optional(),
  canonical_name: z.string().min(1, "Required"),
  display_name: z.string(),
  data_type: z.enum(["string", "integer", "float", "boolean"]),
  unit: z.string(),
  required: z.boolean(),
});

const schemaFormSchema = z.object({
  part_type: z.string().min(1, "Part type is required"),
  description: z.string(),
  parent_fk: z.number().nullable(),
  features: z.array(featureSchema),
});

type SchemaFormData = z.infer<typeof schemaFormSchema>;

const emptyFeature = (): SchemaFormData["features"][number] => ({
  canonical_name: "",
  display_name: "",
  data_type: "string",
  unit: "",
  required: false,
});

interface PartSchemaFormModalProps {
  open: boolean;
  onClose: () => void;
  /** When provided, the modal edits this schema; otherwise it creates one. */
  schema?: PartSchema;
  /** Other schemas, for the optional parent dropdown (self excluded upstream). */
  parentOptions: PartSchema[];
}

const PartSchemaFormModal = ({
  open,
  onClose,
  schema,
  parentOptions,
}: PartSchemaFormModalProps) => {
  const isEdit = !!schema;

  const createSchema = useCreatePartSchema();
  const updateSchema = useUpdatePartSchema();
  const addFeatures = useAddFeatures();
  const updateFeatures = useUpdateFeatures();
  const removeFeatures = useRemoveFeatures();

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SchemaFormData>({
    resolver: zodResolver(schemaFormSchema),
    defaultValues: {
      part_type: "",
      description: "",
      parent_fk: null,
      features: [emptyFeature()],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "features",
  });

  // useFieldArray's `field.id` is React's auto-generated key, NOT the DB id —
  // read the persisted `id` from the live form values instead. A feature with
  // a persisted id was loaded from the schema and cannot be renamed (the
  // update endpoint has no canonical_name field), so its name input is locked.
  const watchedFeatures = watch("features");

  // Disable every mutating control while a save is in flight. `isSubmitting`
  // covers the awaited submit handler; OR-ing the individual mutation loading
  // flags keeps the latch up even across the sequential per-feature calls in
  // edit mode (mirrors the disable-while-pending pattern in PartDetailView).
  const isSaving =
    isSubmitting ||
    createSchema.isLoading ||
    updateSchema.isLoading ||
    addFeatures.isLoading ||
    updateFeatures.isLoading ||
    removeFeatures.isLoading;

  // Reset the form whenever the target schema (or open state) changes.
  useEffect(() => {
    if (!open) return;
    if (schema) {
      reset({
        part_type: schema.part_type,
        description: schema.description ?? "",
        parent_fk: schema.parent_fk ?? null,
        features: (schema.features ?? []).map((f) => ({
          id: f.id,
          canonical_name: f.canonical_name,
          display_name: f.display_name ?? "",
          data_type: f.data_type,
          unit: f.unit ?? "",
          required: f.required,
        })),
      });
    } else {
      reset({
        part_type: "",
        description: "",
        parent_fk: null,
        features: [emptyFeature()],
      });
    }
  }, [open, schema, reset]);

  const onSubmit = async (data: SchemaFormData) => {
    try {
      if (!isEdit) {
        await createSchema.mutateAsync({
          part_type: data.part_type.trim(),
          description: data.description.trim() || null,
          parent_fk: data.parent_fk,
          features: data.features.map((f, index) => ({
            canonical_name: f.canonical_name.trim(),
            display_name: f.display_name.trim() || null,
            data_type: f.data_type,
            unit: f.unit.trim() || null,
            position: index,
            required: f.required,
          })),
        });
        NucToast({ message: "Schema created.", type: "success" });
      } else {
        const { added, updated, removed, reordered, metadataChanged } =
          await saveEdits(data, schema!);
        const parts: string[] = [];
        if (added > 0)
          parts.push(`${added} feature${added === 1 ? "" : "s"} added`);
        if (updated > 0)
          parts.push(`${updated} feature${updated === 1 ? "" : "s"} updated`);
        if (removed > 0)
          parts.push(`${removed} feature${removed === 1 ? "" : "s"} removed`);
        if (reordered) parts.push("features reordered");
        if (parts.length === 0 && metadataChanged)
          parts.push("details updated");
        const summary =
          parts.length > 0 ? `: ${parts.join(", ")}` : " (no changes)";
        NucToast({
          message: `Schema "${schema!.part_type}" updated${summary}.`,
          type: "success",
        });
      }
      onClose();
    } catch (err) {
      const detail = extractErrorDetail(err);
      console.error("Failed to save part schema", err);
      NucToast({
        message: `Failed to save schema: ${detail}`,
        type: "error",
        hideProgressBar: false,
      });
    }
  };

  // Edit mode: diff the form against the persisted schema and issue the
  // minimal set of metadata + feature mutations. Feature changes are bucketed
  // into add / update / remove lists and sent to the batch endpoints — one
  // call per bucket instead of one call per feature.
  const saveEdits = async (data: SchemaFormData, original: PartSchema) => {
    const descChanged =
      (original.description ?? "") !== data.description.trim();
    const parentChanged = (original.parent_fk ?? null) !== data.parent_fk;
    if (descChanged || parentChanged) {
      await updateSchema.mutateAsync({
        schemaId: original.id,
        payload: {
          description: data.description.trim() || null,
          parent_fk: data.parent_fk,
        },
      });
    }

    const originalById = new Map(
      (original.features ?? []).map((f) => [f.id, f]),
    );
    const keptIds = new Set<number>();
    const toAdd: PartSchemaFeatureCreate[] = [];
    const toUpdate: PartSchemaFeatureUpdateItem[] = [];

    // Positions are rewritten when features were reordered or added (an add can
    // require compacting sparse positions left by earlier removals).
    const keptFormIds = data.features
      .filter((f) => f.id != null && originalById.has(f.id))
      .map((f) => f.id as number);
    const keptOriginalOrder = [...keptFormIds].sort(
      (a, b) =>
        (originalById.get(a)?.position ?? 0) -
        (originalById.get(b)?.position ?? 0),
    );
    const orderChanged = keptFormIds.some(
      (id, i) => id !== keptOriginalOrder[i],
    );
    const hasAdds = data.features.some(
      (f) => f.id == null || !originalById.has(f.id),
    );
    const needsRenumber = orderChanged || hasAdds;

    // Report "reordered" only for a genuine relative-order change.
    const reordered = orderChanged;
    // Count only genuine content edits as "updated".
    let contentUpdatedCount = 0;

    data.features.forEach((f, index) => {
      if (f.id != null && originalById.has(f.id)) {
        keptIds.add(f.id);
        const orig = originalById.get(f.id)!;
        const contentChanged =
          (orig.display_name ?? "") !== f.display_name.trim() ||
          orig.data_type !== f.data_type ||
          (orig.unit ?? "") !== f.unit.trim() ||
          orig.required !== f.required;
        const positionChanged = needsRenumber && orig.position !== index;
        if (contentChanged) contentUpdatedCount += 1;
        if (contentChanged || positionChanged) {
          toUpdate.push({
            feature_id: f.id,
            display_name: f.display_name.trim() || null,
            data_type: f.data_type,
            unit: f.unit.trim() || null,
            required: f.required,
            // Only rewrite position when the order actually changed.
            ...(positionChanged ? { position: index } : {}),
          });
        }
      } else {
        toAdd.push({
          canonical_name: f.canonical_name.trim(),
          display_name: f.display_name.trim() || null,
          data_type: f.data_type,
          unit: f.unit.trim() || null,
          position: index,
          required: f.required,
        });
      }
    });

    // Features present originally but dropped from the form.
    const toRemove = (original.features ?? [])
      .filter((f) => !keptIds.has(f.id))
      .map((f) => f.id);

    if (toAdd.length > 0) {
      await addFeatures.mutateAsync({ schemaId: original.id, features: toAdd });
    }
    if (toUpdate.length > 0) {
      await updateFeatures.mutateAsync({
        schemaId: original.id,
        updates: toUpdate,
      });
    }
    if (toRemove.length > 0) {
      await removeFeatures.mutateAsync({
        schemaId: original.id,
        featureIds: toRemove,
      });
    }

    return {
      added: toAdd.length,
      updated: contentUpdatedCount,
      removed: toRemove.length,
      reordered,
      metadataChanged: descChanged || parentChanged,
    };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? "Edit Part Schema" : "New Part Schema"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Part Type"
              size="small"
              fullWidth
              required
              disabled={isEdit || isSaving} // part_type is the schema's identity
              error={!!errors.part_type}
              helperText={
                errors.part_type?.message ??
                (isEdit ? "Part type cannot be changed." : undefined)
              }
              {...register("part_type")}
            />
            <TextField
              label="Description"
              size="small"
              fullWidth
              multiline
              minRows={2}
              disabled={isSaving}
              {...register("description")}
            />
            <Controller
              control={control}
              name="parent_fk"
              render={({ field }) => (
                <FormControl size="small" fullWidth disabled={isSaving}>
                  <InputLabel>Parent Schema (optional)</InputLabel>
                  <Select
                    label="Parent Schema (optional)"
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                  >
                    <MenuItem value="">None</MenuItem>
                    {parentOptions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.part_type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  Features
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => append(emptyFeature())}
                  disabled={isSaving}
                >
                  Add Feature
                </Button>
              </Stack>
              <Stack spacing={1.5}>
                {fields.map((field, index) => {
                  const isExistingFeature =
                    isEdit && !!watchedFeatures?.[index]?.id;
                  return (
                    <Stack
                      key={field.id}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                    >
                      <TextField
                        label="Canonical name"
                        size="small"
                        sx={{ flex: 1.2 }}
                        disabled={isExistingFeature || isSaving}
                        error={
                          !isExistingFeature &&
                          !!errors.features?.[index]?.canonical_name
                        }
                        helperText={
                          isExistingFeature
                            ? "Cannot rename an existing feature."
                            : errors.features?.[index]?.canonical_name?.message
                        }
                        {...register(`features.${index}.canonical_name`)}
                      />
                      <TextField
                        label="Display name"
                        size="small"
                        sx={{ flex: 1.2 }}
                        disabled={isSaving}
                        {...register(`features.${index}.display_name`)}
                      />
                      <Controller
                        control={control}
                        name={`features.${index}.data_type`}
                        render={({ field: typeField }) => (
                          <FormControl
                            size="small"
                            sx={{ minWidth: 110 }}
                            disabled={isSaving}
                          >
                            <InputLabel>Type</InputLabel>
                            <Select label="Type" {...typeField}>
                              {DATA_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>
                                  {t}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                      <TextField
                        label="Unit"
                        size="small"
                        sx={{ width: 90 }}
                        disabled={isSaving}
                        {...register(`features.${index}.unit`)}
                      />
                      <Controller
                        control={control}
                        name={`features.${index}.required`}
                        render={({ field: reqField }) => (
                          <FormControlLabel
                            sx={{ mt: 0.5, whiteSpace: "nowrap" }}
                            control={
                              <Checkbox
                                size="small"
                                checked={reqField.value}
                                disabled={isSaving}
                                onChange={(e) =>
                                  reqField.onChange(e.target.checked)
                                }
                              />
                            }
                            label="Req"
                          />
                        )}
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => remove(index)}
                        sx={{ mt: 0.5 }}
                        aria-label="Remove feature"
                        disabled={isSaving}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  );
                })}
                {fields.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No features yet — add one above.
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSaving}
            startIcon={
              isSaving ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {isEdit ? "Save Changes" : "Create Schema"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PartSchemaFormModal;
